/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import * as angular from 'angular';
import tinycolor from 'tinycolor2';
import { colorToHex } from 'utils/color-utils';
angular.module('ticketspaceApp').factory('firebaseProjectColors', ProjectColorFactory);
ProjectColorFactory.$inject = ['$q', '$firebaseArray', 'SystemColors', '$timeout'];
function ProjectColorFactory($q, $firebaseArray, SystemColors, $timeout) {
	/**
	 * @class Color
	 * @desc A thing wrapper class which represents a Color object
	 * @param snap {DataSnapshot} - A firebase data snapshot
	 * @constructor
	 * @private
	 * @memberOf firebaseProjectColors
	 */
	function Color(snap) {
		this.$id = snap.key;
		this.update(snap);
	}
	Color.prototype = {
		update(snap) {
			let oldData = this.data || {};
			this.data = snap.val();
			if (!this.data.id) {
				this.data.id = snap.key;
			}
			if (this.data.color) {
				this.data.color = this.data.color.toLowerCase();
			}
			return !angular.equals(this.data, oldData);
		},
	};
	/**
	 * @namespace firebaseProjectColors
	 * @desc AngularFire service to manage project colors
	 * @memberOf ticketspaceApp
	 * @constructor
	 */
	function ProjectColors(ref) {
		this.colorPalette = [[]];
		this._usedColors = {};
		this.editColorsModel = {
			active: false,
			startColor: null,
			currentColor: null,
			hideColorPicker: true,
			palettePosition: {},
			showAddColorButton: true,
		};
		let thing = $firebaseArray.call(this, ref);
		thing.editColorsModel = this.editColorsModel;
		return thing;
	}
	ProjectColors.prototype = {
		$$added(snap) {
			let newRec = $firebaseArray.prototype.$$added.apply(this, arguments);
			if (!this.colorExists(newRec.color)) {
				this.pushToColorPalette(this.colorPalette, newRec, SystemColors.MAX_COLORS_PER_ROW);
			}
			this.convertUsedColors(newRec.$id, snap.val());
			return new Color(snap);
		},
		$$updated(snap) {
			let record = this.$getRecord(snap.key);
			if (record) {
				let oldColor = record.data.color;
				let newColor = snap.child('color').val();
				this.updatePalette(oldColor, newColor);
			}
			record.update(snap);
		},
		$add(newData) {
			if (!newData.color || !newData) {
				return $q.reject('Must select a color!');
			}
			let self = this;
			if (!self.colorExists(newData.color)) {
				return $firebaseArray.prototype.$add.call(self, newData).then(function (snap) {
					Logging.notice('New color has been added!');
					return snap.ref.update({ id: snap.key }).then(() => snap);
				});
			} else {
				Logging.warning('Must select a color!');
				return $q.reject('Must select a color!');
			}
		},
		handleAddColor(newColor) {
			let self = this;
			return $q(function (resolve, reject) {
				let record = self.getRecordByColor(newColor);
				if (!record) {
					self.cleanupPalette();
					self.$add({ color: newColor }).then(function (snap) {
						self.resetEditModel();
						if (self.$list.length >= SystemColors.MAX_COLORS_PER_PALETTE) {
							self.editColorsModel.showAddColorButton = false;
						}
						resolve(self.$getRecord(snap.key));
					});
				} else {
					resolve(record);
				}
			});
		},
		$$removed(snap) {
			this.removeColorFromPalette(this.colorPalette, snap.child('color').val());
			if (this.$list.length <= SystemColors.MAX_COLORS_PER_PALETTE) {
				this.editColorsModel.showAddColorButton = true;
			}
			return $firebaseArray.prototype.$$removed.call(this, snap);
		},
		getRecordByColor(color) {
			color = colorToHex(color);
			for (let i = 0; i < this.$list.length; i++) {
				const dataColor = colorToHex(this.$list[i].data.color);
				if (dataColor === color) {
					return this.$list[i];
				}
			}
			return null;
		},
		getColor(id) {
			let rec = this.$getRecord(id);
			return rec ? rec.data.color : '#000000';
		},
		addNewUsedColor(color) {
			if (!this._usedColors[color]) {
				this._usedColors[color] = 1;
			} else {
				this._usedColors[color]++;
			}
		},
		removeUsedColor(color) {
			if (this._usedColors[color] === 1) {
				delete this._usedColors[color];
			} else {
				this._usedColors[color]--;
			}
		},
		updateUsedColors(action, colorId, oldColorId) {
			if (!colorId || !action) {
				return;
			}
			this.forceConvertUsedColorsToHexString();
			let self = this;
			let usedColorsApi = {
				add(newColor) {
					if (!self._usedColors[newColor]) {
						self._usedColors[newColor] = 1;
					} else {
						self._usedColors[newColor]++;
					}
				},
				remove(newColor) {
					if (self._usedColors[newColor] === 1) {
						delete self._usedColors[newColor];
					} else {
						self._usedColors[newColor]--;
					}
				},
				change(newColor, oldColor) {
					if (newColor !== oldColor) {
						this.remove(oldColor);
						this.add(newColor);
					}
				},
			};
			if (action === 'change') {
				usedColorsApi[action](colorId, oldColorId);
			} else {
				let rec = this.$getRecord(colorId);
				if (!rec) {
					usedColorsApi[action](colorId, oldColorId);
				} else {
					this.convertUsedColors(colorId, rec);
					usedColorsApi[action](rec.data.color, oldColorId);
				}
			}
		},
		updateColor(oldColor, newColor) {
			if (!this.colorExists(newColor)) {
				let self = this;
				let record = this.getRecordByColor(oldColor);
				if (record) {
					if (this._usedColors[oldColor]) {
						this.updateUsedColors('change', newColor, oldColor);
					}
					return this.$ref()
						.child(record.$id)
						.update({ color: newColor })
						.then(function () {
							self.resetEditModel();
							Logging.notice('Color has been updated!');
						});
				} else {
					return this.handleAddColor(newColor);
				}
			} else {
				Logging.warning('Color already exists!');
			}
			return null;
		},
		convertUsedColors(id, rec) {
			rec = rec || this.$getRecord(id);
			if (this._usedColors[id]) {
				let count = this._usedColors[id];
				delete this._usedColors[id];
				let c = rec.data ? rec.data.color : rec.color;
				this._usedColors[colorToHex(c)] = count;
			}
		},
		updatePalette(oldColor, newColor) {
			let pos = this.getColorPalettePosition(oldColor);
			if (pos) {
				this.colorPalette[pos.row][pos.index] = colorToHex(newColor);
			}
		},
		updatePaletteAtPosition(row, index, newColor) {
			if (angular.isUndefined(row) || angular.isUndefined(index)) {
				return;
			}
			if (this.colorPalette[row][index] !== newColor) {
				this.colorPalette[row][index] = colorToHex(newColor);
			}
		},
		getColorPalettePosition(color) {
			for (let row = 0; row < this.colorPalette.length; row++) {
				let idx = this.colorPalette[row].indexOf(color);
				if (idx >= 0) {
					return {
						row,
						index: idx,
					};
				}
			}
			return false;
		},
		getUsedColors() {
			return this._usedColors;
		},
		colorExists(color) {
			for (let i = 0; i < this.$list.length; i++) {
				if (this.$list[i].data.color === color) {
					return true;
				}
			}
			return false;
		},
		getColorPalette() {
			return this.colorPalette;
		},
		getFirstUnusedColor() {
			for (let i = 0; i < this.$list.length; i++) {
				let color = this.$list[i].data.color;
				if (!this._usedColors[color]) {
					return color;
				}
			}
			return this.$list[0] ? this.$list[0].data.color : '#000000';
		},
		pushToColorPalette(palette, colorData, maxItemsPerRow) {
			if (!palette || !colorData || !colorData.color) {
				return;
			}
			colorData.color = colorToHex(colorData.color.toLowerCase());
			// Only allow legitimate colors into the palette.
			let parseColor: any = tinycolor(colorData.color);
			if (!parseColor._ok) {
				return;
			}
			let row = palette.length - 1;
			if (palette[row].length >= maxItemsPerRow) {
				row++;
				palette.push([]);
				palette[row].push(colorData.color);
			} else {
				palette[row].push(colorData.color);
			}
			return palette;
		},
		removeColorFromPalette(palette, color) {
			if (!palette || !color) {
				return;
			}
			let pos = this.getColorPalettePosition(color);
			if (pos) {
				palette[pos.row].splice(pos.index, 1);
			}
		},
		cleanupPalette() {
			let row = this.colorPalette.length;
			for (row < 0; row--; ) {
				let idx = this.colorPalette[row].length;
				for (idx < 0; idx--; ) {
					let c = this.colorPalette[row][idx];
					if (!this.getRecordByColor(c)) {
						this.removeColorFromPalette(this.colorPalette, c);
					}
				}
				if (this.colorPalette[row].length === 0) {
					this.colorPalette.splice(row, 1);
				}
			}
		},
		editColorsBucketChanged() {
			if (this.editColorsModel.active) {
				this.updatePaletteAtPosition(
					this.editColorsModel.palettePosition.row,
					this.editColorsModel.palettePosition.index,
					this.editColorsModel.startColor
				);
				this.editSetColorModel(this.editColorsModel.currentColor);
				this.cleanupPalette();
			}
		},
		editSetColorModel(c) {
			this.editColorsModel.palettePosition = this.getColorPalettePosition(c);
			this.editColorsModel.startColor = c;
			this.editColorsModel.currentColor = c;
			this.editColorsModel.hideColorPicker = false;
		},
		toggleEditColors() {
			if (this.$list.length >= SystemColors.MAX_COLORS_PER_PALETTE) {
				this.editColorsModel.showAddColorButton = false;
			}
			this.editColorsModel.active = !this.editColorsModel.active;
			if (!this.editColorsModel.active) {
				this.revertEdit();
			}
			this.forceConvertUsedColorsToHexString();
		},
		forceConvertUsedColorsToHexString() {
			let self = this;
			this.$list.forEach(function (rec) {
				self.convertUsedColors(rec.$id, rec);
			});
		},
		revertEdit() {
			this.updatePaletteAtPosition(
				this.editColorsModel.palettePosition.row,
				this.editColorsModel.palettePosition.index,
				this.editColorsModel.startColor
			);
		},
		onEditChoose() {
			this.updateColor(this.editColorsModel.startColor, this.editColorsModel.currentColor);
		},
		onEditColorChanged(newColor) {
			if (newColor && this.editColorsModel.palettePosition.row >= 0) {
				this.updatePaletteAtPosition(
					this.editColorsModel.palettePosition.row,
					this.editColorsModel.palettePosition.index,
					newColor
				);
			}
		},
		onCancelEditColor() {
			let self = this;
			$timeout(function () {
				self.revertEdit();
				self.editColorsModel.active = false;
				self.resetEditModel();
				self.cleanupPalette();
			}, 0);
		},
		resetEditModel() {
			let self = this;
			$timeout(function () {
				self.editColorsModel.currentColor = null;
				self.editColorsModel.hideColorPicker = true;
				self.editColorsModel.palettePosition = false;
			}, 1);
		},
		onAddColorClicked() {
			if (this.$list.length >= SystemColors.MAX_COLORS_PER_PALETTE) {
				return;
			}
			if (this.editColorsModel.palettePosition) {
				this.updatePaletteAtPosition(
					this.editColorsModel.palettePosition.row,
					this.editColorsModel.palettePosition.index,
					this.editColorsModel.startColor
				);
				this.cleanupPalette();
			} else {
				this.cleanupPalette();
			}
			let self = this;
			$timeout(function () {
				let c = '#' + ('00000' + ((Math.random() * (1 << 24)) | 0).toString(16)).slice(-6);
				self.pushToColorPalette(self.colorPalette, { color: c }, SystemColors.MAX_COLORS_PER_ROW);
				self.editSetColorModel(c);
			}, 0);
		},
	};
	return $firebaseArray.$extend(ProjectColors);
}
